/**
 * CatalogImportApi.ts — Façade API pour l'EF `catalog-import` (Phase D4c).
 *
 * Couvre les opérations de scan et d'import de catalogue via l'Edge Function
 * `catalog-import` (scan LLM vision + commit + rollback + quota).
 *
 * Toutes les méthodes retournent Result<T, AppError>.
 * Aucun import React ni Capacitor.
 *
 * Choix EF justifié (ADR 0001) :
 *   1. Appel LLM vision externe (OpenAI) côté serveur — service_role requis
 *   2. Effets de bord : création import_batch en DB + quota journalier
 *   3. Opération admin/super_admin uniquement
 *   4. Aucun RPC équivalent disponible
 *
 * Invariants :
 *   4.A.10 (exception documentée) : tenant_id cible super_admin — le JWT
 *     super_admin authentifie l'appelant, le tenant_id identifie la cible.
 *   4.A.15 — imageBase64 et pdfText non loggés (données potentiellement sensibles).
 *   4.B.2  — zéro import supabase dans src/components/ ou src/pages/.
 *   4.B.4  — 0 any, types stricts.
 *
 * Call sites attendus (migration D4c) :
 *   - src/components/admin/TenantProductsPanel.tsx (handleCatalogDrop, handleRollback)
 */

import { supabase } from '@/integrations/supabase/client';
import { ok, err } from '@/api/result';
import type { Result } from '@/api/result';
import { appErrorFromSupabase, validationError, supabaseError } from '@/api/errors';
import type { AppError } from '@/api/errors';
import {
  importCatalogScanMenuInputSchema,
  importCatalogScanPdfInputSchema,
  importCatalogScanResultSchema,
  rollbackCatalogImportInputSchema,
  rollbackCatalogImportResultSchema,
  getCatalogImportQuotaInputSchema,
  catalogImportQuotaResultSchema,
} from '../types';
import type {
  ImportCatalogScanMenuInput,
  ImportCatalogScanPdfInput,
  ImportCatalogScanResult,
  RollbackCatalogImportInput,
  RollbackCatalogImportResult,
  GetCatalogImportQuotaInput,
  CatalogImportQuotaResult,
} from '../types';

export const CatalogImportApi = {
  /**
   * Lance un scan de menu (image) via l'EF `catalog-import`.
   *
   * Résultat : importId + produits détectés (preview avant commit).
   * Le caller doit utiliser CatalogScanPreviewDialog pour confirmer
   * avant que les produits soient insérés en DB.
   *
   * Invariant 4.A.15 : imageBase64 non loggé.
   *
   * @param input Données d'entrée (tenantId, imageBase64, mimeType, fileName).
   * @returns Result<ImportCatalogScanResult, AppError>
   */
  async importCatalogFromImage(
    input: ImportCatalogScanMenuInput,
  ): Promise<Result<ImportCatalogScanResult, AppError>> {
    const parsed = importCatalogScanMenuInputSchema.safeParse(input);
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données scan invalides',
        ),
      );
    }

    try {
      const { data, error } = await supabase.functions.invoke('catalog-import', {
        body: {
          action: 'scan-menu',
          tenant_id: parsed.data.tenantId,
          imageBase64: parsed.data.imageBase64,
          mimeType: parsed.data.mimeType,
          fileName: parsed.data.fileName,
        },
      });

      if (error) {
        return err(appErrorFromSupabase(error, 'CatalogImportApi.importCatalogFromImage'));
      }

      if (data?.error) {
        return err(supabaseError('catalog_import_scan_menu', String(data.error)));
      }

      const parsedResult = importCatalogScanResultSchema.safeParse({
        importId: data?.importId,
        scanned: data?.scanned,
      });
      if (!parsedResult.success) {
        return err(
          supabaseError('catalog_import_scan_menu', 'Réponse EF catalog-import malformée'),
        );
      }
      return ok(parsedResult.data);
    } catch (cause) {
      const message =
        cause instanceof Error ? cause.message : 'Erreur inattendue catalog-import image';
      return err(supabaseError('catalog_import_scan_menu', message));
    }
  },

  /**
   * Lance un scan de menu (texte PDF extrait) via l'EF `catalog-import`.
   *
   * Même sémantique que importCatalogFromImage mais pour les PDF avec texte extractible.
   * Pour les PDF scannés (image-only), rasteriser d'abord et utiliser importCatalogFromImage.
   *
   * @param input Données d'entrée (tenantId, pdfText, fileName).
   * @returns Result<ImportCatalogScanResult, AppError>
   */
  async importCatalogFromPdf(
    input: ImportCatalogScanPdfInput,
  ): Promise<Result<ImportCatalogScanResult, AppError>> {
    const parsed = importCatalogScanPdfInputSchema.safeParse(input);
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données scan PDF invalides',
        ),
      );
    }

    try {
      const { data, error } = await supabase.functions.invoke('catalog-import', {
        body: {
          action: 'scan-pdf',
          tenant_id: parsed.data.tenantId,
          pdfText: parsed.data.pdfText,
          fileName: parsed.data.fileName,
        },
      });

      if (error) {
        return err(appErrorFromSupabase(error, 'CatalogImportApi.importCatalogFromPdf'));
      }

      if (data?.error) {
        return err(supabaseError('catalog_import_scan_pdf', String(data.error)));
      }

      const parsedResult = importCatalogScanResultSchema.safeParse({
        importId: data?.importId,
        scanned: data?.scanned,
      });
      if (!parsedResult.success) {
        return err(supabaseError('catalog_import_scan_pdf', 'Réponse EF catalog-import malformée'));
      }
      return ok(parsedResult.data);
    } catch (cause) {
      const message =
        cause instanceof Error ? cause.message : 'Erreur inattendue catalog-import PDF';
      return err(supabaseError('catalog_import_scan_pdf', message));
    }
  },

  /**
   * Annule un import catalog (rollback soft-delete NF525-safe).
   *
   * Les produits créés lors de l'import sont désactivés (active=false).
   * Les catégories créées lors de l'import sont supprimées si elles n'ont pas
   * d'autres produits liés.
   *
   * NF525 safe : pas de hard delete sur products — active=false préserve les
   * références sale_items existantes (invariants 4.A.8, 4.A.14).
   *
   * @param input Données d'entrée (tenantId, importId).
   * @returns Result<RollbackCatalogImportResult, AppError>
   */
  async rollbackCatalogImport(
    input: RollbackCatalogImportInput,
  ): Promise<Result<RollbackCatalogImportResult, AppError>> {
    const parsed = rollbackCatalogImportInputSchema.safeParse(input);
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données rollback invalides',
        ),
      );
    }

    try {
      const { data, error } = await supabase.functions.invoke('catalog-import', {
        body: {
          action: 'rollback',
          tenant_id: parsed.data.tenantId,
          importId: parsed.data.importId,
        },
      });

      if (error) {
        return err(appErrorFromSupabase(error, 'CatalogImportApi.rollbackCatalogImport'));
      }

      if (data?.error) {
        return err(supabaseError('catalog_import_rollback', String(data.error)));
      }

      const parsedResult = rollbackCatalogImportResultSchema.safeParse({
        productsDeactivated: Number(data?.products_deactivated ?? 0),
        categoriesDeleted: Number(data?.categories_deleted ?? 0),
      });
      if (!parsedResult.success) {
        return err(
          supabaseError('catalog_import_rollback', 'Réponse EF catalog-import rollback malformée'),
        );
      }
      return ok(parsedResult.data);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Erreur inattendue rollback catalog';
      return err(supabaseError('catalog_import_rollback', message));
    }
  },

  /**
   * Récupère le statut du quota journalier de scans catalog.
   *
   * Utilisé pour afficher "Scans 3/10" dans l'UI dropzone.
   *
   * @param input Données d'entrée (tenantId).
   * @returns Result<CatalogImportQuotaResult, AppError>
   */
  async getCatalogImportQuota(
    input: GetCatalogImportQuotaInput,
  ): Promise<Result<CatalogImportQuotaResult, AppError>> {
    const parsed = getCatalogImportQuotaInputSchema.safeParse(input);
    if (!parsed.success) {
      const firstIssue = parsed.error.issues[0];
      return err(
        validationError(
          firstIssue?.path.join('.') ?? 'unknown',
          firstIssue?.message ?? 'Données quota invalides',
        ),
      );
    }

    try {
      const { data, error } = await supabase.functions.invoke('catalog-import', {
        body: { action: 'quota-status', tenant_id: parsed.data.tenantId },
      });

      if (error) {
        return err(appErrorFromSupabase(error, 'CatalogImportApi.getCatalogImportQuota'));
      }

      if (data?.limit === undefined) {
        return err(supabaseError('catalog_import_quota', 'Réponse quota malformée'));
      }

      const parsedResult = catalogImportQuotaResultSchema.safeParse({
        used: Number(data.used ?? 0),
        limit: Number(data.limit),
      });
      if (!parsedResult.success) {
        return err(supabaseError('catalog_import_quota', 'Réponse quota invalide'));
      }
      return ok(parsedResult.data);
    } catch (cause) {
      const message = cause instanceof Error ? cause.message : 'Erreur inattendue quota catalog';
      return err(supabaseError('catalog_import_quota', message));
    }
  },
} as const;
